import { t } from '@lingui/macro'
import { ClickAwayListener, Menu, MenuItem } from '@mui/material'
import React, { useRef, useState } from 'react'

export interface InlineDropdownOption<T = string> {
    value: T
    label: string
    disabled?: boolean
}

export interface InlineDropdownProps<T = string> {
    /**
     * The current selected value
     */
    value: T
    /**
     * Array of options to display in the dropdown
     */
    options: InlineDropdownOption<T>[]
    /**
     * Callback fired when an option is selected
     */
    onSelect: (value: T) => void | Promise<void>
    /**
     * Custom render function for the trigger element
     * If not provided, displays the label of the selected option
     */
    renderTrigger?: (value: T, isOpen: boolean) => React.ReactNode
    /**
     * Whether the dropdown is disabled
     */
    disabled?: boolean
    /**
     * Whether the operation is in progress (shows loading state)
     */
    loading?: boolean
    /**
     * Additional CSS class name for the trigger element
     */
    className?: string
    /**
     * Filter function to determine which options to show
     * By default, shows all options except the current value
     */
    filterOptions?: (option: InlineDropdownOption<T>, currentValue: T) => boolean
}

/**
 * InlineDropdown - A clickable inline dropdown component that opens a menu on click
 *
 * @example
 * ```tsx
 * <InlineDropdown
 *   value={currentStatus}
 *   options={[
 *     { value: 'active', label: 'Active' },
 *     { value: 'inactive', label: 'Inactive' }
 *   ]}
 *   onSelect={async (newValue) => {
 *     await updateStatus(newValue)
 *   }}
 * />
 * ```
 */
export function InlineDropdown<T = string>({
    value,
    options,
    onSelect,
    renderTrigger,
    disabled = false,
    loading = false,
    className = '',
    filterOptions
}: InlineDropdownProps<T>) {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
    const [isProcessing, setIsProcessing] = useState(false)
    const triggerRef = useRef<HTMLDivElement>(null)

    const isOpen = Boolean(anchorEl)

    // Default filter: exclude current value
    const defaultFilter = (option: InlineDropdownOption<T>, currentValue: T) => {
        return option.value !== currentValue
    }

    const filterFn = filterOptions || defaultFilter
    const filteredOptions = options.filter((option) => filterFn(option, value))

    // Find the current option's label
    const currentOption = options.find((opt) => opt.value === value)
    const displayLabel = currentOption?.label || String(value)

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        if (disabled || loading || isProcessing) return
        setAnchorEl(event.currentTarget)
    }

    const handleClose = () => {
        setAnchorEl(null)
    }

    const handleSelect = async (optionValue: T) => {
        if (disabled || loading || isProcessing) return

        // Check if the selected option is disabled
        const selectedOption = options.find((opt) => opt.value === optionValue)
        if (selectedOption?.disabled) return

        setIsProcessing(true)
        try {
            await onSelect(optionValue)
            handleClose()
        } catch (error) {
            console.error(t`Error in onSelect callback:`, error)
            // Keep menu open on error so user can retry
        } finally {
            setIsProcessing(false)
        }
    }

    // Default trigger render
    const defaultTrigger = (
        <div
            ref={triggerRef}
            onClick={handleClick}
            className={`inline-flex items-center cursor-pointer ${className} ${
                disabled || loading || isProcessing ? 'opacity-50 cursor-not-allowed' : 'hover:opacity-80'
            }`}
            style={{ userSelect: 'none' }}
        >
            {displayLabel}
            {loading || isProcessing ? <span className='ml-1'>...</span> : <span className='ml-1'>▼</span>}
        </div>
    )

    return (
        <ClickAwayListener onClickAway={handleClose}>
            <div className='inline-block'>
                {renderTrigger ? (
                    <div onClick={handleClick} className={className}>
                        {renderTrigger(value, isOpen)}
                    </div>
                ) : (
                    defaultTrigger
                )}

                <Menu
                    anchorEl={anchorEl}
                    open={isOpen}
                    onClose={handleClose}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'left'
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'left'
                    }}
                    PaperProps={{
                        style: {
                            maxHeight: 300,
                            width: 'auto',
                            minWidth: 150
                        }
                    }}
                >
                    {filteredOptions.length === 0 ? (
                        <MenuItem disabled>{t`No options available`}</MenuItem>
                    ) : (
                        filteredOptions.map((option, index) => (
                            <MenuItem
                                key={option.value === null || option.value === undefined ? `option-${index}` : String(option.value)}
                                onClick={() => handleSelect(option.value)}
                                disabled={option.disabled || loading || isProcessing}
                                sx={{
                                    cursor: option.disabled || loading || isProcessing ? 'default' : 'pointer'
                                }}
                            >
                                {option.label}
                            </MenuItem>
                        ))
                    )}
                </Menu>
            </div>
        </ClickAwayListener>
    )
}

export default InlineDropdown
